/**
 * Single-flight "create the real session for this draft".
 *
 * Paste/drop and the first send both materialize a draft tab. Called
 * independently, a paste landing while the send's `createSession` was in
 * flight (or the reverse) made two chats out of one draft. Every caller goes
 * through `materialize(draftId, create)`: a pending create for the same draft
 * is awaited and reused; a successful result is remembered for that draft id
 * (draft ids are never reused, and a caller holding a stale draft id from the
 * last render must get the same session, not a new one); a null result or a
 * throw is NOT remembered, so the next attempt retries.
 *
 * `done` grows by one short entry per chat created in this view's lifetime —
 * negligible, and dropping entries would reopen the stale-draft-id race.
 *
 * A null key (no tab at all, or HeroChat's single unnamed draft) is only
 * de-duplicated while in flight — nothing identifies a later call as "the same
 * draft".
 */
export interface DraftMaterializer {
  materialize(draftId: string | null, create: () => Promise<string | null>): Promise<string | null>
}

const NO_KEY = '\u0000no-draft'

export function createDraftMaterializer(): DraftMaterializer {
  const pending = new Map<string, Promise<string | null>>()
  const done = new Map<string, string>()
  return {
    materialize(draftId, create) {
      const key = draftId ?? NO_KEY
      const made = draftId ? done.get(key) : undefined
      if (made) return Promise.resolve(made)
      const inFlight = pending.get(key)
      if (inFlight) return inFlight
      const p = (async () => {
        try {
          const sid = await create()
          if (sid && draftId) done.set(key, sid)
          return sid
        } finally {
          pending.delete(key)
        }
      })()
      pending.set(key, p)
      return p
    },
  }
}
